import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { useI18n } from "../../i18n";
import { EASE } from "../../lib/site";

const Card = ({ c, i }) => (
  <motion.a
    href={c.link}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, ease: EASE, delay: i * 0.1 }}
    whileHover={{ y: -6 }}
    data-testid={`social-card-${i}`}
    aria-label={c.title}
    className="group flex flex-col overflow-hidden rounded-2xl border border-ink/10 bg-white shadow-[0_18px_36px_-24px_rgba(26,26,26,0.3)] transition-shadow duration-300 hover:shadow-[0_24px_48px_-20px_rgba(26,26,26,0.4)]"
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-alabaster">
      <img
        src={c.img}
        alt={c.title}
        loading="lazy"
        className="h-full w-full object-cover"
        style={c.imgPos ? { objectPosition: c.imgPos } : undefined}
      />
      {c.play && (
        <span className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-ink/70 text-paper backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
            <Play className="ml-0.5 h-6 w-6 fill-current" />
          </span>
        </span>
      )}
    </div>
    <div className="flex flex-1 flex-col gap-2.5 p-5">
      <h3 className="font-serif text-lg leading-snug text-ink">{c.title}</h3>
      <span className="font-mono text-xs text-muted-ink">{c.tag}</span>
      {c.stats && (
        <div
          data-testid={`social-stats-${i}`}
          className={`mt-1 grid gap-3 rounded-xl border border-ink/10 bg-alabaster/70 px-4 py-3 ${c.stats.length > 2 ? "grid-cols-3" : c.stats.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}
        >
          {c.stats.map((s, j) => (
            <div key={s.l} className={j > 0 ? "border-l border-ink/15 pl-4" : ""}>
              <div data-testid={`social-stat-${i}-${j}`} className="font-serif text-3xl leading-none text-ink">{s.v}</div>
              <div className="mt-1.5 font-mono text-[11px] text-muted-ink">{s.l}</div>
            </div>
          ))}
        </div>
      )}
      {c.caption && <p className="text-sm leading-relaxed text-body-ink">{c.caption}</p>}
      {c.pills && (
        <div className="mt-auto flex flex-wrap gap-2 pt-3">
          {c.pills.map((p) => (
            <span
              key={p.label}
              role="link"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(p.link, "_blank", "noopener"); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); window.open(p.link, "_blank", "noopener"); } }}
              data-testid={`social-pill-${p.label.toLowerCase()}`}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-medium text-terra transition-colors duration-300 hover:bg-terra hover:text-paper"
            >
              {p.label} <ArrowUpRight className="h-3 w-3" />
            </span>
          ))}
        </div>
      )}
      {c.linkLabel && (
        <div className="mt-auto pt-3">
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(c.link, "_blank", "noopener"); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); window.open(c.link, "_blank", "noopener"); } }}
            data-testid={`social-visit-${i}`}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-medium text-terra transition-colors duration-300 hover:bg-terra hover:text-paper"
          >
            {c.linkLabel} <ArrowUpRight className="h-3 w-3" />
          </span>
        </div>
      )}
    </div>
  </motion.a>
);

export const SocialMedia = () => {
  const { t } = useI18n();
  return (
    <div className="py-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {t.social.cards.map((c, i) => <Card key={i} c={c} i={i} />)}
      </div>
    </div>
  );
};
